/**
 * COMPOSANT : sellerie-app/frontend/src/components/Calepinage/DevisCalepinage_cleaned.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
 * Ce composant affiche le calepinage d'un devis.
 * Il utilise le composant CalepinageGlobal pour afficher le calepinage global,
 * et le composant CalepinageVisualisation pour afficher le détail d'un groupe de matières.
 * 
 * MODIFICATION: Utilisation des couleurs réelles des matières dans les vignettes
 * PARTIE B: Logs d'économie supprimés - Le calcul d'économie est maintenant centralisé côté backend
 */

import React, { useState, useEffect, useContext } from 'react';
import './Calepinage.css';
import { Devis } from '../../types/dimensions';
import { Matiere } from '../../pages/Devis/ConfigurateurArticle/types';
import CalepinageVisualisation from './CalepinageVisualisation';
import CalepinageGlobal from './CalepinageGlobal';
import { matiereService } from '../../services/MatiereService';
import { apiService } from '../../services/ApiService';
import { apiFetch } from '../../services/httpClient';
import * as XLSX from 'xlsx';
import { AuthContext } from '../../contexts/AuthContext';

interface DevisCalepinageProps {
  devis: Devis;
  matieres?: Matiere[]; // Nouvellement ajouté pour recevoir les matières du parent
  isRefreshing?: boolean; // indique qu'une optimisation/vignettes est en cours
}

/**
 * Fonction utilitaire pour obtenir la couleur d'une matière
 * ADAPTÉE à  la structure actuelle à couleurs = string[]
 */
const getMatiereCouleur = (matieres: Matiere[], matiereId: string, couleurNom: string): string => {
  // Palette de couleurs par défaut adaptée aux noms français
  const couleursDefaut: {[key: string]: string} = {
    // Couleurs de base
    'blanc': '#f8f9fa',      // Gris très clair au lieu du blanc pur pour la visibilité
    'noir': '#212529',
    'rouge': '#dc3545',
    'bleu': '#0d6efd',
    'vert': '#198754',
    'jaune': '#ffc107',
    'orange': '#fd7e14',
    'violet': '#6f42c1',
    'rose': '#d63384',
    'gris': '#6c757d',
    'marron': '#8b4513',
    'beige': '#f5f5dc',
    
    // Variations
    'bleu marine': '#003366',
    'bleu ciel': '#87ceeb',
    'rouge bordeaux': '#800020',
    'vert olive': '#808000',
    'vert foncé': '#006400',
    'gris clair': '#d3d3d3',
    'gris foncé': '#404040',
    'jaune citron': '#ffff00',
    'orange vif': '#ff4500',
    
    // Couleurs spécifiques sellerie
    'camel': '#c19a6b',
    'chocolat': '#7b3f00',
    'crème': '#fffdd0',
    'écru': '#f4f0ec',
    'anthracite': '#36454f',
    'marine': '#003366',
    'bordeaux': '#800020',
    'cognac': '#9a463d',
    'moka': '#926644',
    'ivoire': '#fffff0',
    'sable': '#c2b280',
    'taupe': '#483c32'
  };
  
  // Normaliser le nom de couleur (minuscules, sans accents)
  const couleurNormalisee = couleurNom.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

  // Rechercher d'abord une correspondance exacte
  if (couleursDefaut[couleurNormalisee]) {

    return couleursDefaut[couleurNormalisee];
  }
  
  // Rechercher une correspondance partielle
  for (const [cle, valeur] of Object.entries(couleursDefaut)) {
    if (couleurNormalisee.includes(cle) || cle.includes(couleurNormalisee)) {

      return valeur;
    }
  }
  
  // Génerer une couleur basée sur le hash du nom pour la cohérence
  let hash = 0;
  for (let i = 0; i < couleurNormalisee.length; i++) {
    hash = couleurNormalisee.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Convertir en couleur hex (couleurs pastel pour meilleure lisibilité)
  const hue = hash % 360;
  const couleurGeneree = `hsl(${hue}, 70%, 75%)`;

  return couleurGeneree;
};

/**
 * Fonction utilitaire pour générer une couleur plus claire pour le fond
 */
const getLighterColor = (hexColor: string, factor: number = 0.3): string => {
  // Convertir hex en RGB
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Éclaircir la couleur
  const newR = Math.min(255, Math.floor(r + (255 - r) * factor));
  const newG = Math.min(255, Math.floor(g + (255 - g) * factor));
  const newB = Math.min(255, Math.floor(b + (255 - b) * factor));
  
  // Reconvertir en hex
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

/**
 * Composant pour afficher le calepinage d'un devis
 */
const DevisCalepinage: React.FC<DevisCalepinageProps> = ({ devis, matieres: matieresFromProps, isRefreshing }) => {
  const [selectedGroupe, setSelectedGroupe] = useState<string | null>(null);
  const [matieres, setMatieres] = useState<Matiere[]>(matieresFromProps || []);
  const [vignettes, setVignettes] = useState<{[key: string]: string}>({});
  const [vignetteComparaison, setVignetteComparaison] = useState<{[key: string]: any}>({});
  const [isLoadingVignettes, setIsLoadingVignettes] = useState<boolean>(true);
  const [zoom, setZoom] = useState<number>(1);
  const [testComparaison, setTestComparaison] = useState<any>(null);
  const [isLoadingComparaison, setIsLoadingComparaison] = useState<boolean>(false);
  const [inversionLayouts, setInversionLayouts] = useState<{[key: string]: {calepinage: any, meta?: any}}>({});
  const [inversionLoadingFor, setInversionLoadingFor] = useState<string | null>(null);
  const { user, getAuthToken } = useContext(AuthContext);
  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  })();
  const isAdmin = (user?.role === 'admin') || (storedUser?.role === 'admin');

  const downloadScrapBand = async (groupeId: string) => {
    const token = getAuthToken ? getAuthToken() : localStorage.getItem('authToken');
    if (!token) {
      console.error("Token manquant pour l'appel scrap_band");
      return;
    }
    try {
      const res = await fetch(`${apiService.getBaseUrl()}/admin/calepinage/scrap-band-png`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ devis_id: devis.id || devis.reference, groupe_id: groupeId }),
      });
      if (!res.ok) throw new Error(`Erreur scrap_band ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${groupeId}_scrap_band.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erreur telechargement scrap_band", error);
    }
  };

  const runInversionLaize = async (groupeId: string) => {
    const token = getAuthToken ? getAuthToken() : localStorage.getItem('authToken');
    if (!token) {
      console.error("Token manquant pour l'appel inversion_laize");
      return;
    }
    setInversionLoadingFor(groupeId);
    try {
      const res = await fetch(`${apiService.getBaseUrl()}/admin/calepinage/inversion-laize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          devis_id: devis.id || devis.reference,
          groupe_id: groupeId,
          mode: 'inversion_laize_v2'
        }),
      });
      const data = await res.json();
      if (data.status === 'ok') {
        setInversionLayouts(prev => ({
          ...prev,
          [groupeId]: { calepinage: data.calepinage, meta: data.meta }
        }));

        // Télécharger automatiquement le PNG (même logique que scrap_band)
        try {
          const resPng = await fetch(`${apiService.getBaseUrl()}/admin/calepinage/inversion-laize-png`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              devis_id: devis.id || devis.reference,
              groupe_id: groupeId,
              mode: 'inversion_laize_v2'
            }),
          });

          if (!resPng.ok) {
            const errorText = await resPng.text();
            alert(`Téléchargement PNG inversion échoué (${resPng.status}) : ${errorText || 'réponse vide'}`);
            return;
          }

          const contentType = resPng.headers.get('Content-Type') || '';
          if (!contentType.includes('image')) {
            const errorText = await resPng.text();
            alert(`Réponse inattendue pour le PNG (type=${contentType || 'inconnu'}) : ${errorText || 'réponse vide'}`);
            return;
          }

          const blob = await resPng.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${groupeId}_inversion.png`;
          a.click();
          URL.revokeObjectURL(url);
        } catch (err) {
          console.error("Erreur téléchargement PNG inversion", err);
          alert("Erreur réseau lors du téléchargement du PNG d'inversion");
        }
      } else {
        const raison = data.raison || 'invalide';
        const meta = data.meta || {};
        const W = meta.W ?? meta.laize_reelle ?? '?';
        const H2 = meta.H2 ?? meta.H2_before_rotation ?? '?';
        const widthFinal = meta.width_final_after_rotation ?? meta.width_final ?? '?';
        const message = `Inversion V2 rejetée : H2=${H2} > W=${W} (raison=${raison})` +
          (widthFinal !== '?' ? `, width_final=${widthFinal}` : '');
        alert(message);
      }
    } catch (error) {
      console.error("Erreur inversion_laize", error);
      alert("layout inversé invalide : erreur réseau");
    } finally {
      setInversionLoadingFor((current) => current === groupeId ? null : current);
    }
  };

  /**
   * Teste les nouvelles fonctionnalités de comparaison de calepinage
   */
  const testerComparaison = async () => {
    if (!devis.groupesMatiere || devis.groupesMatiere.length === 0) {
      alert("Aucun groupe de matières disponible pour le test");
      return;
    }
    
    const premierGroupe = devis.groupesMatiere[0];
    const groupeId = `${premierGroupe.matiere}|||${premierGroupe.couleur}`;
    const devisId = devis.id || devis.reference || 'test';
    
    setIsLoadingComparaison(true);
    
    try {
      const data = await apiFetch<any>(`/calepinage/${groupeId}/comparaison?devis_id=${devisId}`);

      setTestComparaison(data);
      
      // Afficher un résumé dans une alerte
      const compact = data.compact;
      const aligne = data.aligne;
      
      alert(`Test réussi ! 
      
Calepinage Compact:
- Longueur: ${compact.longueur_totale}cm
- Taux de chute: ${(compact.taux_chute * 100).toFixed(1)}%
- Traits communs: ${compact.traits_communs}

Calepinage Aligné:
- Longueur: ${aligne.longueur_totale}cm  
- Taux de chute: ${(aligne.taux_chute * 100).toFixed(1)}%
- Traits communs: ${aligne.traits_communs}

Recommandation: ${data.recommandation}`);
      
    } catch (error) {
      console.error("Erreur lors du test de comparaison:", error);
      alert(`Erreur lors du test: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setIsLoadingComparaison(false);
    }
  };
  
  /**
   * Génère un fichier Excel avec les détails du calepinage
   */
  const generateExcel = () => {
    // [Le reste du code generateExcel reste identique...]
    // Créer un nouveau classeur
    const workbook = XLSX.utils.book_new();
    
    // Créer un compteur pour chaque combinaison article/type de pièce
    const compteursPieces: {[key: string]: number} = {};
    
    // Préparer les données pour la feuille "Détail des pièces"
    const detailPieces = devis.groupesMatiere.flatMap(groupe => 
      groupe.pieces.map(piece => {
        // Trouver l'article correspondant
        const article = devis.articles.find(a => a.id === piece.articleId);
        
        // Extraire le type de pièce
        const pieceType = piece.type;
        
        // Déterminer si la pièce est pivotée dans le calepinage
        let pivote = false;
        if (groupe.calepinage) {
          const placement = groupe.calepinage.placement.find(
            p => p.pieceId === piece.id || (p as any).piece_id === piece.id
          );
          if (placement) {
            pivote = placement.rotated;
          }
        }
        
        // Construire le nom de la pièce
        let nomPiece = '';
        switch (pieceType) {
          case 'dessus':
            nomPiece = 'Plateau dessus';
            break;
          case 'fond':
            nomPiece = 'Plateau dessous';
            break;
          case 'bandeau':
            nomPiece = 'Bandeau';
            break;
          case 'joue1':
            nomPiece = 'Joue 1';
            break;
          case 'joue2':
            nomPiece = 'Joue 2';
            break;
          case 'cylindre':
            nomPiece = 'Cylindre';
            break;
          default:
            nomPiece = pieceType;
        }
        
        // Récupérer le nom de la matière
        const matiere = matieres.find(m => m.id === piece.matiere);
        const nomMatiere = matiere ? matiere.nom : piece.matiere;
        
        // Obtenir les dimensions de la pièce
        let dimensions = '';
        const dims = piece.dimensions || {};
        
        // [Le reste de la logique des dimensions reste identique...]
        // ... (code omis pour la brièveté)
        
        return {
          'Article': article?.nom || `Article ${piece.articleId}`,
          'Pièce': nomPiece,
          'Dimensions (cm)': dimensions,
          'Surface (m²)': piece.surface.toFixed(3),
          'Matière': nomMatiere,
          'Couleur': piece.couleur,
          'Pivoté': pivote ? 'Oui' : 'Non'
        };
      })
    );
    
    // Créer la feuille "Détail des pièces"
    const detailSheet = XLSX.utils.json_to_sheet(detailPieces);
    XLSX.utils.book_append_sheet(workbook, detailSheet, "Détail des pièces");
    
    // Préparer les données pour la feuille "Synthèse des matières"
    const syntheseMatieres = devis.groupesMatiere.map(groupe => {
      // Récupérer le nom de la matière
      const matiere = matieres.find(m => m.id === groupe.matiere);
      const nomMatiere = matiere ? matiere.nom : groupe.matiere;
      
      // Calculer la surface totale
      const surfaceTotale = groupe.pieces.reduce((sum, p) => sum + p.surface, 0);
      
      // Récupérer les informations du calepinage
      const longueur = groupe.calepinage ? groupe.calepinage.longueurTotale : 0;
      const tauxChute = groupe.calepinage ? groupe.calepinage.tauxChute * 100 : 0;
      const economie = groupe.pourcentageEconomie || 0;
      
      return {
        'Matière': nomMatiere,
        'Couleur': groupe.couleur,
        'Laize (cm)': groupe.laize,
        'Longueur (cm)': longueur.toFixed(1),
        'Surface totale (m²)': surfaceTotale.toFixed(3),
        'Taux de chute (%)': tauxChute.toFixed(1),
        'Économie (%)': economie.toFixed(1)
      };
    });
    
    // Créer la feuille "Synthèse des matières"
    const syntheseSheet = XLSX.utils.json_to_sheet(syntheseMatieres);
    XLSX.utils.book_append_sheet(workbook, syntheseSheet, "Synthèse des matières");
    
    // Générer le fichier Excel
    const reference = devis.reference || "sans-reference";
    XLSX.writeFile(workbook, `Plan de coupe ${reference}.xlsx`);
  };

  useEffect(() => {
    const fetchMatieres = async () => {
      // Si les matières sont déjà  fournies par les props, les utiliser
      if (matieresFromProps && matieresFromProps.length > 0) {
        setMatieres(matieresFromProps);
        return;
      }
      
      try {
        const matieresData = await matiereService.getMatieres();
        setMatieres(matieresData);
      } catch (error) {
        console.error("Erreur lors du chargement des matieres:", error);
      }
    };
    
    fetchMatieres();
  }, [matieresFromProps]);
  
  // Logs supprimés - Le calcul d'économie est maintenant centralisé côté backend
  
  // 🎨 NOUVELLE LOGIQUE : Charger les vignettes avec les couleurs réelles des matières
  useEffect(() => {
    setIsLoadingVignettes(true);
    
    // Utiliser l'ID du devis ou sa référence comme identifiant
    const devisId = devis.id || devis.reference;



    // Vérifier si le devis a des groupes de matières
    if (!devis.groupesMatiere || devis.groupesMatiere.length === 0) {

      setIsLoadingVignettes(false);
      return;
    }
    
    // Générer des vignettes avec les couleurs réelles des matières
    const vignettesGenerees: {[key: string]: string} = {};
    
    // Pour chaque groupe de matières, générer une vignette avec la couleur réelle
    devis.groupesMatiere.forEach(groupe => {
      if (!groupe.matiere || !groupe.couleur) return;
      
      const groupeId = `${groupe.matiere}|||${groupe.couleur}`;

      //  Récupérer la couleur réelle de la matière
      const couleurReelle = getMatiereCouleur(matieres, groupe.matiere, groupe.couleur);
      const couleurFond = getLighterColor(couleurReelle, 0.8);

      // Générer une vignette avec la couleur réelle
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 200;
        canvas.height = 150;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
          // Fond général avec quadrillage
          ctx.fillStyle = '#f8f9fa';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Dessiner un quadrillage léger sur tout le canvas
          ctx.strokeStyle = '#e9ecef';
          ctx.lineWidth = 0.5;
          const gridSize = 10;
          
          // Lignes verticales
          for (let x = 0; x <= canvas.width; x += gridSize) {
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, canvas.height);
            ctx.stroke();
          }
          
          // Lignes horizontales
          for (let y = 0; y <= canvas.height; y += gridSize) {
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(canvas.width, y);
            ctx.stroke();
          }
          
          // Fond de la laize avec la couleur éclaircite de la matière
          ctx.fillStyle = couleurFond;
          ctx.fillRect(10, 10, canvas.width - 20, canvas.height - 20);
          ctx.strokeStyle = '#dee2e6';
          ctx.lineWidth = 2;
          ctx.strokeRect(10, 10, canvas.width - 20, canvas.height - 20);
          
          // Dessiner des rectangles pour représenter les pièces avec la couleur réelle
          if (groupe.pieces && groupe.pieces.length > 0) {
            // Calculer l'échelle
            const maxWidth = canvas.width - 40;
            const maxHeight = canvas.height - 40;
            const laize = groupe.laize || 100;
            const longueurTotale = groupe.calepinage?.longueurTotale || 100;
            
            const scaleX = maxWidth / laize;
            const scaleY = maxHeight / longueurTotale;
            const scale = Math.min(scaleX, scaleY);
            
            // Dessiner les pièces avec la couleur réelle de la matière
            groupe.pieces.forEach(piece => {
              // Trouver le placement de la pièce
              const placement = groupe.calepinage?.placement?.find(
                p => p.pieceId === piece.id || (p as any).piece_id === piece.id
              );
              if (!placement) return;
              
              // Utiliser la couleur réelle de la matière
              ctx.fillStyle = couleurReelle;
              ctx.fillRect(
                20 + placement.x * scale,
                20 + placement.y * scale,
                placement.width * scale,
                placement.height * scale
              );
              
              // Contour leger pour delimiter les pieces
              ctx.strokeStyle = '#000000';
              ctx.lineWidth = 0.5;
              ctx.strokeRect(
                20 + placement.x * scale,
                20 + placement.y * scale,
                placement.width * scale,
                placement.height * scale
              );
            });
          }
          
          // Ajouter un indicateur de couleur en bas a droite
          ctx.fillStyle = couleurReelle;
          ctx.fillRect(canvas.width - 30, canvas.height - 20, 20, 10);
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 1;
          ctx.strokeRect(canvas.width - 30, canvas.height - 20, 20, 10);
          
          // Convertir le canvas en base64
          const dataURL = canvas.toDataURL('image/png');
          vignettesGenerees[groupeId] = dataURL.split(',')[1];

        }
      } catch (error) {
        console.error("Erreur generation vignette:", error);
      }
    });
    
    console.log("Vignettes generees:", Object.keys(vignettesGenerees));
    setVignettes(vignettesGenerees);
    setIsLoadingVignettes(false);
    
    // Essayer de charger les vignettes depuis le backend si disponible
    if (devisId) {
      const ts = Date.now();
      apiFetch<{[key: string]: string}>(`/devis/${devisId}/calepinage/vignettes?ts=${ts}`)
        .then(data => {
          if (data && Object.keys(data).length > 0) {
            setVignettes(data);
          }
        })
        .catch(err => {
          console.error("Erreur backend, utilisation des vignettes locales:", err);
        });
    }
  }, [devis, matieres]); // 🎨 Ajouter matieres comme dépendance
  

  // Filtrer les groupes qui ont un calepinage
  const groupesAvecCalepinage = devis.groupesMatiere.filter(g => g.calepinage && g.pieces.length > 0);
  
  // Trouver le groupe sélectionné ou utiliser le premier
  const groupeActifBase = selectedGroupe 
    ? groupesAvecCalepinage.find(g => `${g.matiere}|||${g.couleur}` === selectedGroupe) 
    : groupesAvecCalepinage[0];
  const groupeActifId = groupeActifBase ? `${groupeActifBase.matiere}|||${groupeActifBase.couleur}` : null;
  const groupeActif = React.useMemo(() => {
    if (!groupeActifBase || !groupeActifId) return groupeActifBase;
    const override = inversionLayouts[groupeActifId];
    if (override) {
      return { ...groupeActifBase, calepinage: override.calepinage };
    }
    return groupeActifBase;
  }, [groupeActifBase, groupeActifId, inversionLayouts]);

  if (isRefreshing) {
    return (
      <div className="bg-white shadow rounded-lg p-6 mb-4">
        <h2 className="text-lg font-medium mb-2">Calepinage</h2>
        <p className="text-gray-600">Optimisation en cours, génération des vignettes…</p>
      </div>
    );
  }

  if (groupesAvecCalepinage.length === 0) {
    // Vérifier si nous avons des groupes de matières mais sans calepinage
    if (devis.groupesMatiere.length > 0) {
      return (
        <div className="bg-white shadow rounded-lg p-6 mb-4">
          <h2 className="text-lg font-medium mb-4">Calepinage</h2>
          <p className="text-gray-600">
            L'optimisation du calepinage n'a pas pu être calculée. 
            Assurez-vous que les articles ont des dimensions valides et que l'optimisation est activée.
          </p>
        </div>
      );
    }
    
    return (
      <div className="bg-white shadow rounded-lg p-6 mb-4">
        <h2 className="text-lg font-medium mb-4">Calepinage</h2>
        <p className="text-gray-600">Aucun calepinage disponible pour ce devis.</p>
      </div>
    );
  }
  
  if (!groupeActif) {
    return null;
  }
  

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-4">
      <h2 className="text-lg font-medium mb-4">Calepinage et optimisation</h2>
      
      {/* Aperçu des vignettes pour toutes les matières avec contrôles de zoom intégrés */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-md font-medium">Aperçu des matières</h3>
          
      {/* Contrôles de zoom + action admin scrap_band */}
      {selectedGroupe && (
        <div className="flex items-center gap-3">
          <div className="flex items-center">
            <span className="text-sm font-medium mr-2">Zoom:</span>
            <button
              className="px-2 py-1 bg-gray-200 rounded-l hover:bg-gray-300"
              onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
            >
              -
            </button>
            <span className="px-3 py-1 bg-gray-100 text-sm">
              {Math.round(zoom * 100)}%
            </span>
            <button
              className="px-2 py-1 bg-gray-200 rounded-r hover:bg-gray-300"
              onClick={() => setZoom(Math.min(3, zoom + 0.1))}
            >
              +
            </button>
          </div>
          {isAdmin && (
          <div className="flex items-center gap-2">
              {(() => {
                const overrideMeta = inversionLayouts[selectedGroupe]?.meta;
                const groupeMeta = (groupeActifBase as any)?.calepinage_meta || {};
                const metaMode = overrideMeta?.calepinage_mode || groupeMeta.calepinage_mode || 'baseline';
                const metaValidation = overrideMeta?.validation || groupeMeta.validation || 'ok';
                const metaReason = overrideMeta?.reason || groupeMeta.reason || null;
                const label = metaMode === 'baseline' ? 'Calepinage original'
                  : metaMode === 'inversion_laize' ? 'Inversion laize'
                  : metaMode === 'scrap_band' ? 'Scrap band'
                  : metaMode;
                const title = metaReason
                  ? `Validation ${metaValidation} (${metaReason})`
                  : `Validation ${metaValidation}`;
                return (
                  <span
                    className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 border border-gray-200"
                    title={title}
                  >
                    Mode: {label}
                  </span>
                );
              })()}
              <button
                className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-xs font-medium disabled:opacity-60"
                disabled={inversionLoadingFor === selectedGroupe}
                onClick={() => runInversionLaize(selectedGroupe)}
              >
                {inversionLoadingFor === selectedGroupe ? 'Inversion...' : 'Inversion laize (admin)'}
              </button>
              <button
                className="px-3 py-1 bg-amber-600 text-white rounded-md hover:bg-amber-700 text-xs font-medium"
                onClick={() => downloadScrapBand(selectedGroupe)}
              >
                Scrap_band PNG
              </button>
            </div>
          )}
        </div>
      )}
        </div>
        
        {isLoadingVignettes ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {groupesAvecCalepinage.map(groupe => {
              const groupeId = `${groupe.matiere}|||${groupe.couleur}`;
              const vignette = vignettes[groupeId];
              
              // Récupérer le nom de la matière
              const matiere = matieres.find(m => m.id === groupe.matiere);
              const nomMatiere = matiere ? matiere.nom : groupe.matiere;
              
              // Récupérer la couleur réelle pour l'indicateur
              const couleurReelle = getMatiereCouleur(matieres, groupe.matiere, groupe.couleur);
              const calepinageAffiche = (inversionLayouts[groupeId]?.calepinage) || groupe.calepinage;

              return (
                <div 
                  key={groupeId}
                  className={`border rounded-lg p-2 cursor-pointer hover:bg-gray-50 transition ${selectedGroupe === groupeId ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
                  onClick={() => setSelectedGroupe(groupeId)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-medium truncate flex-1">
                      {nomMatiere} - {groupe.couleur}
                    </div>
                    {/* Indicateur de couleur réelle */}
                    <div 
                      className="w-4 h-4 rounded border border-gray-300 ml-2 flex-shrink-0"
                      style={{ backgroundColor: couleurReelle }}
                      title={`Couleur: ${groupe.couleur}`}
                    />
                  </div>
                  
                  {vignette ? (
                    <div className="relative aspect-w-4 aspect-h-3 bg-gray-100 rounded overflow-hidden">
                      <img 
                        src={`data:image/png;base64,${vignette}`} 
                        alt={`Aperçu ${nomMatiere} - ${groupe.couleur}`}
                        className="object-contain w-full h-full"
                      />
                    </div>
                  ) : (
                    <div className="aspect-w-4 aspect-h-3 bg-gray-100 rounded flex items-center justify-center">
                      <span className="text-gray-400 text-xs">Pas d'aperçu</span>
                    </div>
                  )}
                  
                  <div className="mt-1 text-xs text-gray-500">
                    <div>Laize: {groupe.laize} cm</div>
                    <div>Longueur: {(calepinageAffiche?.longueurTotale || calepinageAffiche?.longueur_totale || 0).toFixed(1)} cm</div>
                    <div>Chute: {(calepinageAffiche?.tauxChute ? (calepinageAffiche.tauxChute * 100).toFixed(1) : '0.0')}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Visualisation du calepinage pour le groupe sélectionné */}
      {selectedGroupe && groupeActif && (
        <CalepinageVisualisation 
          groupe={groupeActif} 
          devisId={devis.id || devis.reference || ''} 
          zoom={zoom}
          onZoomChange={setZoom}
        />
      )}
      
    </div>
  );
};

export default DevisCalepinage;
